<script setup lang="ts">
import type { HeaderProps } from '.';

import { Button } from '@/components/ui/button';
import { cn } from '@/utils';
import * as typeform from '@typeform/embed';
import { useEvents } from '~/composables/events/useEvents';
import { useShortsNavigation } from '~/composables/shorts';
import { useLoginModal } from '~/composables/useLoginModal';
import { blogUrlHeader } from '~/constants/seo';
import { useNavigationStore } from '~/store/navigationStore';
import { USER_OPTION_SYMBOL, USER_SYMBOL } from '~~/utils/symbols';

import { GlassButton } from '~/components/GlassButton';

import IconMaterialSymbolsLightPersonOutline from '~icons/material-symbols-light/person-outline';
import LinearPilarLogoRaw from '~icons/pilar/linear-logo?raw&width=1em&height=1em';
import IconPilarMenu from '~icons/pilar/menu';

import { loginBackgroundVariants, navVariants } from './index';
import messages from './PortalHeader.locale.json';

// Lazy hydration para o menu mobile - só hidrata em telas < 1024px
const LazyPortalHeaderNavMobile = defineLazyHydrationComponent(
	'mediaQuery',
	() => import('./PortalHeaderNavMobile.vue')
);

// Lazy hydration para o menu desktop - só hidrata em telas >= 1024px
const LazyPortalHeaderNav = defineLazyHydrationComponent(
	'mediaQuery',
	() => import('./PortalHeaderNav.vue')
);

// Lazy hydration para o menu de perfil mobile - só hidrata em telas < 1024px
const LazyPortalHeaderNavMobileProfile = defineLazyHydrationComponent(
	'mediaQuery',
	() => import('./PortalHeaderNavMobileProfile.vue')
);

const emit = defineEmits<{
	(e: 'nav-option-being-shown'): void;
	(e: 'nav-option-closed'): void;
}>();

const props = withDefaults(defineProps<HeaderProps>(), {
	isVisible: true,
	transform: 'none',
	variant: 'primary',
	fixed: false,
	hasHeaderOverlay: false,
	links: () => [
		{
			route: blogUrlHeader,
			label: 'Blog',
			openInNewTab: true
		},
		{
			route: '/sobre-a-pilar',
			label: 'Sobre a Pilar'
		}
	],
	contextualHeaderLinks: () => []
});

const { loggedIn, fetch: fetchSession, user } = useUserSession();

const navigationStore = useNavigationStore();
const { isMenuOpen } = storeToRefs(navigationStore);

const { navigateToShortsWithContext } = useShortsNavigation();

function handleOpenFeed() {
	trackHeader.handleEventHeaderClick('Shorts');
	navigateToShortsWithContext();
}

const typeformPopup = ref<ReturnType<typeof typeform.createPopup>>();

const { leadBrokerTypeformId } = useRuntimeConfig().public;

const route = useRoute();
const router = useRouter();

onMounted(() => {
	typeformPopup.value = typeform.createPopup(leadBrokerTypeformId, {
		onClose: () => {
			router.replace({
				...route,
				query: { ...route.query, typeform: undefined }
			});
		}
	});
});

const handleOpenTypeform = () => {
	typeformPopup.value?.open();
};

const { t } = useI18n({
	useScope: 'local',
	messages
});
const { trackHeader } = useEvents();
const { openLoginModal } = useLoginModal();
const { ft_shorts_access_points } = useFeatureFlags();

/**
 * Handles login button click with tracking and modal opening
 * Uses double requestAnimationFrame to ensure tracking runs AFTER paint
 * This prevents blocking the INP metric
 */
function handleLoginClick(): void {
	// Open modal first for immediate visual feedback
	openLoginModal();
	// Double rAF ensures we yield to browser for paint, then run tracking
	// First rAF: scheduled before next paint
	// Second rAF: scheduled after that paint completes
	requestAnimationFrame(() => {
		requestAnimationFrame(() => {
			trackHeader.handleEventHeaderClick('Entrar');
		});
	});
}

onMounted(async () => {
	if (loggedIn.value) return;

	await fetchSession();
});

provide(USER_SYMBOL, user);

watch(isMenuOpen, isMenuOpen => {
	if (isMenuOpen) {
		emit('nav-option-being-shown');
	} else {
		emit('nav-option-closed');
	}
});

watch(
	() => props.isVisible,
	isVisible => navigationStore.setIsHeaderVisible(isVisible),
	{ immediate: true }
);

watch(
	route,
	currentRoute => {
		if (currentRoute.query?.typeform === 'lead-broker') {
			handleOpenTypeform();
		}

		if (currentRoute.query?.openLoginModal === 'true') {
			openLoginModal();
			router.replace({
				...currentRoute,
				query: { ...currentRoute.query, openLoginModal: undefined }
			});
		}
	},
	{ immediate: true }
);

const styledLogo = (LinearPilarLogoRaw as unknown as string).replace(
	/<svg/,
	'<svg width="100%" height="100%"'
);

const wrapperClasses = computed(() =>
	navVariants({
		variant: props.variant,
		transform: props.fixed ? 'none' : props.transform
	})
);
</script>

<!-- eslint-disable vue/no-v-html -->
<template>
	<nav
		class="z-50 flex w-full items-center"
		:class="[
			wrapperClasses,
			{
				'-translate-y-full':
					!fixed && !isVisible && !navigationStore.isMenuMobileOpen
			},
			props.class
		]"
	>
		<div
			v-if="hasHeaderOverlay"
			v-show="variant === 'translucide'"
			class="absolute z-10 size-full bg-linear-to-b from-black/60 to-transparent"
		/>
		<section
			class="relative z-20 h-[60px] w-full items-center justify-between px-4 lg:flex lg:h-20 lg:px-12"
		>
			<LazyMediaQueryPortalHeaderNavMobile
				:variant="variant"
				class="lg:hidden"
				hydrate-on-media-query="(max-width: 1023px)"
			/>

			<div class="flex size-full items-center gap-x-4 lg:gap-x-11">
				<IconPilarMenu
					class="block size-6 min-h-6 min-w-6 cursor-pointer text-white lg:hidden"
					@click="
						navigationStore.setIsMobileMenuOpen(
							!navigationStore.isMobileMenuOpen
						)
					"
				/>

				<NuxtLink
					to="/"
					aria-label="Página inicial"
					@click="trackHeader.handleEventHeaderClick('Logo', '/')"
				>
					<div class="w-[132px] lg:h-5 lg:w-[164px]" v-html="styledLogo" />
				</NuxtLink>

				<div class="flex size-full items-center justify-between gap-x-6">
					<LazyMediaQueryPortalHeaderNav
						class="hidden lg:block"
						hydrate-on-media-query="(min-width: 1024px)"
					/>

					<div class="ml-auto flex h-full items-center gap-x-6">
						<div class="hidden flex-row items-center gap-x-6 lg:flex">
							<NuxtLink
								v-for="link in links"
								:key="`header-link: ${link.label}`"
								:to="link.route"
								:target="link.openInNewTab ? '_blank' : undefined"
								:external="link.openInNewTab"
								class="cursor-pointer whitespace-nowrap text-base font-normal leading-[150%] text-white"
								@click="
									trackHeader.handleEventHeaderClick(link.label, link.route)
								"
							>
								{{ link.label }}
							</NuxtLink>
						</div>

						<HeaderVideoButton
							v-if="ft_shorts_access_points"
							@open-feed="handleOpenFeed"
						/>

						<!-- ClientOnly ensures auth state is only rendered on client to improve cache hit ratio -->
						<ClientOnly>
							<template #fallback>
								<!-- SSR fallback: Always show login button -->
								<GlassButton shape="square" size="auto" class="gap-1 px-3 py-2">
									<IconMaterialSymbolsLightPersonOutline
										class="size-5 text-beige"
									/>
									<span class="text-sm font-medium leading-[20px] text-white">{{
										t('login')
									}}</span>
								</GlassButton>
							</template>

							<!-- Client: Show profile or login based on actual auth state -->
							<div
								v-if="loggedIn"
								class="ml-auto flex h-full items-center gap-x-2 lg:ml-0"
							>
								<PortalHeaderNavProfile
									variant="default"
									:bg-variant="variant"
									@open-profile-menu="
										navigationStore.setIsMobileProfileMenuOpen(true)
									"
								/>
								<LazyMediaQueryPortalHeaderNavMobileProfile
									hydrate-on-media-query="(max-width: 1023px)"
								/>
							</div>

							<GlassButton
								v-if="!loggedIn"
								shape="square"
								size="auto"
								class="gap-1 px-3 py-2 transition-opacity active:opacity-70"
								@click="handleLoginClick"
							>
								<IconMaterialSymbolsLightPersonOutline
									class="size-5 text-beige"
								/>
								<span class="text-sm font-medium leading-[20px] text-white">{{
									t('login')
								}}</span>
							</GlassButton>
						</ClientOnly>
					</div>
				</div>
			</div>
		</section>
	</nav>
</template>
